import { Button, Card, Col, Modal, Row, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import LineItemsDetailBar from './LineItemsDetailBar';
import { TableColumnTitle } from '../../../../../../../shared/globalStyling/styledText';
import { TagWithConfig } from '../../../../../shared/StatusTag';
import DualAxisChart from '../../../../../../../shared/charts/DualAxisChart';
import { computeCTR, formatNumber } from '../../../../../../../../core/utils/campaigns';
import { isEmpty, sum } from 'lodash';
import moment from 'moment';
import CreativeTable from '../../../../components/creatives/components/CreativeCTRTable';
import SegmentsTable from '../../../../components/segments/SegmentsTable';
import { tagStatusConfig } from '../../../../utils';
import { AreaChartOutlined } from '@ant-design/icons';

const summaryScale = (data, leftY, rightY) => {
  const scale = {
    left:
      leftY === 'impressions'
        ? {
            type: 'linear',
            min: 0,
            max: !isEmpty(data) ? data['impressions'] : 1000,
            alias: 'Impressions',
            formatter: (val) => {
              return `${formatNumber(val)}`;
            },
          }
        : leftY === 'clicks'
          ? {
              type: 'linear',
              min: 0,
              max: !isEmpty(data) ? data['clicks'] : 0,
              alias: 'Clicks',
              formatter: (val) => {
                return `${formatNumber(val)}`;
              },
            }
          : {},
    right:
      rightY === 'impressions'
        ? {
            type: 'linear',
            min: 0,
            max: !isEmpty(data) ? data['impressions'] : 1000,
            alias: 'Impressions',
            formatter: (val) => {
              return `${formatNumber(val)}`;
            },
          }
        : rightY === 'clicks'
          ? {
              type: 'linear',
              min: 0,
              max: !isEmpty(data) ? data['clicks'] : 0,
              alias: 'Clicks',
              formatter: (val) => {
                return `${formatNumber(val)}`;
              },
            }
          : {},
    xAxis: {
      tickCount: 8,
    },
  };
  return scale;
};

const LineItemsModal = ({
  byDate = [], // By date data for the chart
  summary = {
    startDate: '2025-01-01',
    endDate: '2026-01-01',
    impressions: 0,
    clicks: 0,
    ctr: 0,
  },
  creatives = [], // Creatives data for the table
  publishers = [], // Publishers data for the table
  segments = [], // Segments data for the table
  name = 'Line Item', // Name of the line item
  status = 'PLAY', // Status of the line item
}) => {
  const [visible, setVisible] = useState(false);
  // Calculate initial summary data based on the full date range
  const calculateInitialSummary = useCallback(() => {
    const filteredData = byDate.filter(
      (data) =>
        moment(data.date, 'YYYY-MM-DD').isSameOrAfter(moment(summary.startDate)) &&
        moment(data.date, 'YYYY-MM-DD').isSameOrBefore(moment(summary.endDate))
    );

    return filteredData.reduce(
      (acc, data) => {
        acc.impressions += data.impressions;
        acc.clicks += data.clicks;
        acc.ctr = computeCTR(acc.clicks, acc.impressions);
        return acc;
      },
      {
        impressions: 0,
        clicks: 0,
        ctr: 0,
      }
    );
  }, [byDate, summary.startDate, summary.endDate]);

  const [summaryData, setSummaryData] = useState(() => calculateInitialSummary());
  const hasInitialized = useRef(false);

  const dropDownOptions = [
    { key: 'impressions', display: 'Impressions' },
    { key: 'clicks', display: 'Transactions' },
  ];

  const onDatePickerChange = useCallback(
    (dates) => {
      const filteredData = byDate.filter(
        (data) =>
          moment(data.date, 'YYYY-MM-DD').isSameOrAfter(dates[0]) &&
          moment(data.date, 'YYYY-MM-DD').isSameOrBefore(dates[1])
      );

      const summary = filteredData.reduce(
        (acc, data) => {
          acc.impressions += data.impressions;
          acc.clicks += data.clicks;
          acc.ctr = computeCTR(acc.clicks, acc.impressions);
          return acc;
        },
        {
          impressions: 0,
          clicks: 0,
          ctr: 0,
        }
      );

      setSummaryData(summary);
    },
    [byDate]
  );

  // Update summaryData when byDate or summary props change
  useEffect(() => {
    setSummaryData(calculateInitialSummary());
  }, [calculateInitialSummary]);

  useEffect(() => {
    if (visible && !hasInitialized.current) {
      hasInitialized.current = true;
      const timeoutId = setTimeout(() => {
        onDatePickerChange([moment(summary.startDate), moment(summary.endDate)]);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    if (!visible) {
      hasInitialized.current = false;
    }
  }, [visible, onDatePickerChange, summary.startDate, summary.endDate]);

  return (
    <>
      <Tooltip title="View Charts">
        <Button type="link" onClick={() => setVisible(true)}>
          <AreaChartOutlined />
        </Button>
      </Tooltip>

      <Modal
        visible={visible}
        width={'85%'}
        footer={null}
        onCancel={() => setVisible(false)}
        title={
          <Row>
            <TableColumnTitle text={name} />
            <TagWithConfig {...tagStatusConfig[status]} />
          </Row>
        }
      >
        <Row>
          <Col xs={24}>
            <LineItemsDetailBar
              startDate={summary.startDate}
              endDate={summary.endDate}
              impressions={summaryData.impressions}
              clicks={summaryData.clicks}
              ctr={summaryData.ctr}
            />
          </Col>
          <Col xs={24}>
            <DualAxisChart
              byDate={byDate}
              xAxis="date"
              options={dropDownOptions}
              defaultLeftYValue={dropDownOptions[0]}
              defaultRightYValue={dropDownOptions[1]}
              customScale={summaryScale}
              onDatePickerChange={({ data, dates, rangeKey }) => {
                onDatePickerChange(dates);
              }}
              has6MonDisables={false}
              allTimeDateRange={[moment(summary.startDate), moment(summary.endDate)]}
            />
          </Col>
          <Col xs={24}>
            <Card title={'Creatives'} bordered={false}>
              <CreativeTable
                creativeData={creatives.map((c) => ({
                  ...c,
                  value: c.creativeName,
                  ctr: computeCTR(c.clicks, c.impressions),
                }))}
              />
            </Card>
          </Col>
          {/* Temporary hidden until the publisher info has been properly resolved */}
          {/* <Col xs={24}>
            <Card title={"Publishers"} bordered={false}>
              <PublisherCTRTable
                publisherData={publishers.map(p => ({
                  ...p,
                  value: p.publisherName,
                  ctr: computeCTR(p.clicks, p.impressions),
                }))}
              />
            </Card>
          </Col> */}
          <Col xs={24}>
            <Card title={'Segments'} bordered={false}>
              <SegmentsTable segmentsData={segments} />
            </Card>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default LineItemsModal;
